import type { ReactNode } from "react";
import type { AddressValue, FormRowTone } from "../types.js";
import { AddressInput } from "./AddressInput.js";
import "./styles.css";

export interface ReadOnlyValueProps {
  /**
   * The value to display, already formatted for reading. The caller decides how to present it
   * (humanised option label, formatted number, etc.); this component only displays it.
   */
  value?: ReactNode;
  /**
   * Phase 572 wave 6 task 6.2 — the value as already-described LINES (a display format preset's
   * read-back, e.g. one classification code per line), shown as a list. Takes precedence over
   * {@link value}. Plain text: each line is rendered as text, never as markup.
   */
  lines?: ReadonlyArray<string>;
  /**
   * Phase 572 wave 6 task 6.2 — a structured address, shown as the EXISTING read-only address
   * block (`AddressInput`'s prefilled arm) rather than a second address layout. Takes precedence
   * over {@link lines} and {@link value}.
   */
  address?: AddressValue;
  /** The DOM id of the presentation — the id the caller's FieldRow label points at. Optional. */
  id?: string;
  /** Walk 2 — the tone a plain {@link value} reads as (a pill in the feedback colour). Optional. */
  tone?: FormRowTone;
  /**
   * An optional plain-English sentence explaining why the field is read-only and who may change
   * it. When present, it is shown under the value. Contains no field name, gate id or role token.
   */
  sentence?: string;
}

/** An address block never changes here: the one no-op every read-only address shares. */
const IGNORE_CHANGE = () => {};

/**
 * Phase 572 wave 3 task 3.2 — the ONE no-control value presentation.
 *
 * <p><b>Declaration-agnostic.</b> This component has no knowledge of gates, roles or `read_only`
 * declarations. It simply displays a value with an optional sentence; the CALLER decides when to
 * use it (a locked decision field, a declared read-only field, etc.).</p>
 *
 * <p><b>A CONTROL inside FieldRow, never its own row.</b> The label stays on FieldRow; the source
 * chip stays where FormRenderer renders it (beside the control). This is only the value
 * presentation — one place where a fact is shown rather than edited.</p>
 *
 * <p><b>Phase 572 wave 6 task 6.2 — extended, not duplicated (Reconciliation R3).</b> A declared
 * read_only field needs two more SHAPES of already-formatted value: described lines (a list) and
 * a structured address (the existing read-only address block). Both land here as props, so
 * there is still exactly one no-control presentation; a caller passing only `value` renders
 * exactly what it did before.</p>
 *
 * <p>The served value is still seeded into the form model and travels in the submitted payload
 * through the existing `from_ctx_value` mechanism. This component is purely presentational.</p>
 */
export function ReadOnlyValue({ value, lines, address, id, sentence, tone }: ReadOnlyValueProps) {
  let shown: ReactNode;
  if (address !== undefined) {
    shown = <AddressInput id={id ? `${id}__address` : "read-only-address"} value={address} onChange={IGNORE_CHANGE} prefilled />;
  } else if (lines !== undefined) {
    shown = (
      <ul className="read-only-value-lines">
        {/* Index-keyed: the same entry twice is a legal (if pointless) answer. */}
        {lines.map((line, i) => <li className="read-only-value-line" key={`${i}-${line}`}>{line}</li>)}
      </ul>
    );
  } else {
    // Walk 2 — a toned value reads as the same pill a toned display row does.
    shown = (
      <p className="read-only-value-text">
        {tone !== undefined ? <span className={`display-tone display-tone-${tone}`}>{value}</span> : value}
      </p>
    );
  }
  return (
    <div className="read-only-value" id={id}>
      {shown}
      {sentence && <p className="read-only-value-sentence">{sentence}</p>}
    </div>
  );
}
